import { isPlatformBrowser } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    PLATFORM_ID,
    signal,
    viewChild,
} from '@angular/core';

import { TabType } from '../../_shared/types/tab.type';
import { getRovingIndex } from '../../_shared/utils/roving-index';
import { ContainerComponent } from '../../container/container.component';
import { SpacerComponent } from '../../spacer/spacer.component';
import { nextTabGroupId } from '../tab-group-id';
import { TabComponent } from '../tab.component';

@Component({
    selector: 'sirius-tab-group-v2',
    templateUrl: './tab-group-v2.component.html',
    styleUrls: ['./tab-group-v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ContainerComponent, SpacerComponent],
    host: {
        'class': 'sir-tab-group-v2',
        '[class.sir-small]': "size() === 'small'",
        '[class.is-stuck]': 'stuck()',
    },
})
export class TabGroupV2Component {
    readonly size = input<TabType>('large');
    readonly topSpacer = input(true);

    private readonly _platformId = inject(PLATFORM_ID);
    private readonly _tabs = contentChildren(TabComponent);
    private readonly _sentinel = viewChild<ElementRef<HTMLElement>>('sentinel');
    private readonly _bar = viewChild<ElementRef<HTMLElement>>('bar');

    protected readonly groupId = nextTabGroupId();
    protected readonly selectedIndex = signal(0);
    protected readonly stuck = signal(false);
    protected readonly tabs = computed(() => this._tabs());
    protected readonly activeTabId = computed(() => `${this.groupId}-tab-${this.selectedIndex()}`);

    private _observer?: IntersectionObserver;
    private readonly _onResize = (): void => this.refreshObserver();

    constructor() {
        const destroyRef = inject(DestroyRef);

        // Drive visibility and panel semantics on the projected tabs. Content
        // stays in the DOM (SSR-indexable); `active` only toggles `hidden`.
        effect(() => {
            const tabs = this._tabs();
            const selected = this.selectedIndex();
            tabs.forEach((tab, index) => {
                tab.active = index === selected;
                tab.panelId = `${this.groupId}-panel-${index}`;
                tab.labelledById = `${this.groupId}-tab-${index}`;
            });
        });

        afterNextRender(() => {
            this.refreshObserver();
            window.addEventListener('resize', this._onResize, { passive: true });
        });

        destroyRef.onDestroy(() => {
            this._observer?.disconnect();
            if (isPlatformBrowser(this._platformId)) {
                window.removeEventListener('resize', this._onResize);
            }
        });
    }

    protected onTabClick(index: number): void {
        this.selectedIndex.set(index);
        this.scrollToBar();
    }

    protected onKeydown(event: KeyboardEvent): void {
        const nextIndex = getRovingIndex(event.key, this.selectedIndex(), this._tabs().length, 'horizontal');
        if (nextIndex === null) return;

        event.preventDefault();
        this.selectedIndex.set(nextIndex);

        const tablist = (event.currentTarget as HTMLElement).closest('[role="tablist"]');
        tablist?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]?.focus();
    }

    private refreshObserver(): void {
        if (!isPlatformBrowser(this._platformId) || typeof IntersectionObserver === 'undefined') return;

        const sentinel = this._sentinel()?.nativeElement;
        const bar = this._bar()?.nativeElement;
        if (!sentinel || !bar) return;

        this._observer?.disconnect();

        // The bar pins at `top: var(--sir-nav-height)`; offset the observer root
        // by that amount so `stuck` flips exactly when the bar reaches the nav.
        const offset = Number.parseFloat(getComputedStyle(bar).top) || 0;
        this._observer = new IntersectionObserver(([entry]) => this.stuck.set(!entry.isIntersecting), {
            rootMargin: `-${offset + 1}px 0px 0px 0px`,
            threshold: 0,
        });
        this._observer.observe(sentinel);
    }

    private scrollToBar(): void {
        if (!isPlatformBrowser(this._platformId)) return;

        const prefersReducedMotion =
            typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

        this._bar()?.nativeElement.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    }
}
